/**
 * Logic + costanti condivise tra le 4 varianti catalogo esperienze.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ExcursionItem } from '@/app/esperienze/_views/shared';
import { type ExperienceTypeOption } from '@/lib/experience-types';
import type { ExcursionCategoryOption } from '@/lib/excursion-categories';
import { type ShowcaseConfig, parseShowcaseConfig } from '@/lib/esperienze-showcase';

// Mapping accent/icon per tassonomia 4+4 (2026-06-04).
// Legacy id (grotte-marine/tour-isole/pesca-sportiva/charter-custom)
// retrofittati a 4+4 a livello dati. Mantenuti qui solo come safety
// fallback per record storici non ancora migrati.
export const TYPE_ACCENT: Record<string, { from: string; to: string }> = {
  // 4 escursione
  pescaturismo:           { from: 'from-amber-400/10',    to: 'to-orange-500/[0.04]' },
  'tour-barca':           { from: 'from-emerald-300/10',  to: 'to-cyan-500/[0.04]' },
  snorkeling:             { from: 'from-cyan-400/10',     to: 'to-blue-500/[0.04]' },
  'sport-acquatici':      { from: 'from-sky-400/10',      to: 'to-blue-600/[0.04]' },
  // 4 esperienza
  'sunset-cruise':        { from: 'from-orange-400/12',   to: 'to-pink-500/[0.04]' },
  'chef-on-board':        { from: 'from-rose-400/10',     to: 'to-amber-500/[0.04]' },
  'festa-privata':        { from: 'from-fuchsia-400/10',  to: 'to-pink-600/[0.04]' },
  wellness:               { from: 'from-emerald-400/10',  to: 'to-teal-600/[0.04]' },
  // Legacy safety
  'grotte-marine':        { from: 'from-indigo-400/10',   to: 'to-violet-600/[0.04]' },
  'tour-isole':           { from: 'from-teal-300/10',     to: 'to-emerald-500/[0.04]' },
  'pesca-sportiva':       { from: 'from-red-400/10',      to: 'to-orange-600/[0.04]' },
  'charter-custom':       { from: 'from-blue-400/10',     to: 'to-cyan-600/[0.04]' },
};

export const TYPE_ICON: Record<string, string> = {
  // 4 escursione
  pescaturismo:      '🎣',
  'tour-barca':      '⛵',
  snorkeling:        '🤿',
  'sport-acquatici': '🏄',
  // 4 esperienza
  'sunset-cruise':   '🍾',
  'chef-on-board':   '👨‍🍳',
  'festa-privata':   '🎉',
  wellness:          '🧘',
  // Legacy safety
  'grotte-marine':   '🕳️',
  'tour-isole':      '🏝️',
  'pesca-sportiva':  '🎯',
  'charter-custom':  '🧭',
};

export function formatEUR(n: number): string {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

export type CatalogVariant = 'ecommerce' | 'tabs' | 'carousel' | 'map';
export const CATALOG_VARIANTS: { id: CatalogVariant; label: string; icon: string }[] = [
  { id: 'ecommerce', label: 'E-commerce', icon: '🛒' },
  { id: 'tabs',      label: 'Tabs',       icon: '📑' },
  { id: 'carousel',  label: 'Netflix',    icon: '🎬' },
  { id: 'map',       label: 'Mappa',      icon: '🗺️' },
];

export const COOKIE_KEY = 'esperienze-catalog-variant';

export function getStoredVariant(): CatalogVariant | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(?:^| )' + COOKIE_KEY + '=([^;]+)'));
  if (!m) return null;
  const v = decodeURIComponent(m[1]);
  return (['ecommerce', 'tabs', 'carousel', 'map'] as CatalogVariant[]).includes(v as CatalogVariant)
    ? (v as CatalogVariant)
    : null;
}

export function setStoredVariant(v: CatalogVariant) {
  if (typeof document === 'undefined') return;
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(v)}; max-age=${oneYear}; path=/; SameSite=Lax`;
}

export interface CatalogData {
  excursions: ExcursionItem[];
  loading: boolean;
  activeTypes: Array<ExperienceTypeOption & { count: number; sample: ExcursionItem[] }>;
  groupedByType: Map<string, ExcursionItem[]>;
  showcase: ShowcaseConfig;
}

export function useExperiencesCatalog(types: string = 'escursione,esperienza'): CatalogData {
  const [excursions, setExcursions] = useState<ExcursionItem[]>([]);
  const [categories, setCategories] = useState<ExcursionCategoryOption[]>([]);
  const [showcase, setShowcase] = useState<ShowcaseConfig>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Carica catalog + categorie + config vetrina da DB in parallelo.
    // Categorie sono fonte di verità per i tab; placeholder vuoti tollerati.
    Promise.all([
      // Phase 5 read switch (listings refactor): catalog homepage legge da
      // /api/listings filtered by type=escursione,esperienza.
      // Backward compat: response shape compatibile con ExcursionItem.
      fetch(`/api/listings?type=${encodeURIComponent(types)}&status=PUBLISHED`, { cache: 'no-store' })
        .then((r) => r.json())
        .then((d) => {
          if (d?.success && Array.isArray(d.data)) setExcursions(d.data);
        })
        .catch(() => { /* silent */ }),
      fetch('/api/settings/public/excursion-categories', { cache: 'no-store' })
        .then((r) => r.json())
        .then((d) => {
          if (d?.success && Array.isArray(d.data)) setCategories(d.data);
        })
        .catch(() => { /* silent */ }),
      fetch('/api/settings/public/esperienze-showcase', { cache: 'no-store' })
        .then((r) => r.json())
        .then((d) => {
          if (d?.success && d.data) setShowcase(parseShowcaseConfig(d.data));
        })
        .catch(() => { /* silent */ }),
    ]).finally(() => setLoading(false));
  }, [types]);

  const groupedByType = useMemo(() => {
    const map = new Map<string, ExcursionItem[]>();
    for (const ex of excursions) {
      const cat = ex.category || 'tour-barca';
      const arr = map.get(cat) || [];
      arr.push(ex);
      map.set(cat, arr);
    }
    return map;
  }, [excursions]);

  const activeTypes = useMemo(() => {
    return categories
      .filter((t) => t.enabled !== false)
      .map((t) => {
        const all = groupedByType.get(t.id) || [];
        const premium = all.filter((e) => e.is_premium).slice(0, 3);
        const sample = premium.length > 0 ? premium : all.slice(0, 3);
        // Adapter a ExperienceTypeOption per compat con UI esistente
        const opt: ExperienceTypeOption = {
          id: t.id,
          label: t.label,
          description: t.description,
          enabled: t.enabled,
        };
        return { ...opt, count: all.length, sample };
      });
    // NB: non filtriamo per count>0. Categorie vuote restano visibili come
    // placeholder "In arrivo" per dare visibilità futura.
  }, [groupedByType, categories]);

  return { excursions, loading, activeTypes, groupedByType, showcase };
}
